import { useState, useEffect, useCallback, useRef } from 'react'

function isAbortError(e) {
  return e?.name === 'AbortError' || (typeof DOMException !== 'undefined' && e instanceof DOMException && e.name === 'AbortError')
}

/**
 * Runs fetcher when deps change. Fetcher receives AbortSignal (pass to fetch).
 *
 * Single-flight: starting a new run (effect deps, enabled, or refetch()) aborts the previous
 * in-flight request and ignores its result so refetch() and the effect never apply stale data.
 *
 * @param {(signal: AbortSignal) => Promise<unknown>} fetcher
 * @param {import('react').DependencyList} deps
 * @param {{ initialData?: unknown, enabled?: boolean }} [options]
 *   - initialData: seed value; also restored while disabled (see enabled)
 *   - enabled: when false, no fetch, loading false, data reset to initialData, error cleared; refetch() still runs
 */
export function useAsyncResource(fetcher, deps, options = {}) {
  const { initialData = null, enabled: enabledOption = true } = options
  const enabled = enabledOption !== false

  const [data, setData] = useState(initialData)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState(null)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  const generationRef = useRef(0)
  const activeAbortRef = useRef(null)

  const beginSession = () => {
    activeAbortRef.current?.abort()
    const controller = new AbortController()
    activeAbortRef.current = controller
    generationRef.current += 1
    const myGen = generationRef.current
    return { signal: controller.signal, myGen, controller }
  }

  const refetch = useCallback(async () => {
    const { signal, myGen } = beginSession()
    setLoading(true)
    setError(null)
    try {
      const result = await fetcherRef.current(signal)
      if (signal.aborted || myGen !== generationRef.current) return result
      setData(result)
      setError(null)
      return result
    } catch (e) {
      if (signal.aborted || isAbortError(e) || myGen !== generationRef.current) {
        return undefined
      }
      const message = e instanceof Error ? e.message : String(e)
      setError(message)
      setData(null)
      throw e
    } finally {
      if (!signal.aborted && myGen === generationRef.current) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      activeAbortRef.current?.abort()
      activeAbortRef.current = null
      setLoading(false)
      setError(null)
      setData(initialData)
      return undefined
    }

    const { signal, myGen, controller } = beginSession()

    setLoading(true)
    setError(null)

    Promise.resolve(fetcherRef.current(signal))
      .then((result) => {
        if (signal.aborted || myGen !== generationRef.current) return
        setData(result)
        setError(null)
      })
      .catch((e) => {
        if (signal.aborted || isAbortError(e) || myGen !== generationRef.current) return
        setError(e instanceof Error ? e.message : String(e))
        setData(null)
      })
      .finally(() => {
        if (!signal.aborted && myGen === generationRef.current) {
          setLoading(false)
        }
      })

    return () => {
      controller.abort()
      if (activeAbortRef.current === controller) {
        activeAbortRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- invalidation is the deps argument
  }, [...deps, enabled])

  return { data, loading, error, refetch }
}
